import React, { useState, useEffect, useRef } from 'react';
import { User, LogIn, UserPlus, Crown, PieChart, Settings2, LogOut, Share2, Sun, Moon, Monitor } from 'lucide-react';
import { UserProfile } from '../types';
import { useTheme } from '../hooks/useTheme';
import type { ThemeSetting } from '../utils/theme';

const THEME_OPTIONS: { value: ThemeSetting; label: string; icon: React.ReactNode }[] = [
  { value: 'light', label: 'Light', icon: <Sun size={14} /> },
  { value: 'dark', label: 'Dark', icon: <Moon size={14} /> },
  { value: 'system', label: 'Match the system', icon: <Monitor size={14} /> },
];

/**
 * The account menu: the same on every page it is opened from, in the same words. Signed in it
 * says "Subscription and limits", "Analytics", "Settings" and "Sign out"; over an open
 * workspace that can be shared it says "Share workspace" first (pass onShare).
 */
interface ProfileMenuProps {
  user: UserProfile | null;
  isPro?: boolean;
  onSignIn?: () => void;
  onCreateAccount?: () => void;
  /** Share the open workspace: only where there is one to share (the canvas). */
  onShare?: () => void;
  onSubscription?: () => void;
  onAnalytics?: () => void;
  onSettings?: () => void;
  onSignOut?: () => void;
}

export const ProfileMenu: React.FC<ProfileMenuProps> = ({
  user,
  isPro = false,
  onSignIn,
  onCreateAccount,
  onShare,
  onSubscription,
  onAnalytics,
  onSettings,
  onSignOut,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const focusFirstOnOpen = useRef(false);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (isOpen && containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, [isOpen]);

  // ArrowDown on the trigger opens the menu and moves focus to the first item
  useEffect(() => {
    if (isOpen && focusFirstOnOpen.current) {
      focusFirstOnOpen.current = false;
      panelRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    }
  }, [isOpen]);

  const handleTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key !== 'ArrowDown') return;
    e.preventDefault();
    if (isOpen) {
      panelRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    } else {
      focusFirstOnOpen.current = true;
      setIsOpen(true);
    }
  };

  // Escape closes the menu and returns focus to the trigger
  const handleContainerKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape' && isOpen) {
      e.preventDefault();
      setIsOpen(false);
      triggerRef.current?.focus();
    }
  };

  const initial = user ? user.name.charAt(0).toUpperCase() : '';

  return (
    <div className="relative flex" ref={containerRef} onKeyDown={handleContainerKeyDown}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleTriggerKeyDown}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={user && isPro ? 'Account menu, Pro plan' : 'Account menu'}
        title={user && isPro ? 'Account (Pro)' : 'Account'}
        // The same trigger as the account menu of the canvas: the avatar, or a round button for a
        // guest. A paid plan is a gold ring around the avatar (the top bars show no plan chip for it).
        className={user ? `ns-avatar cursor-pointer transition-shadow hover:shadow-ring${isPro ? ' ring-2 ring-amber-500 ring-offset-2 ring-offset-white' : ''}` : 'ns-icon-btn ns-icon-btn-soft'}
        data-plan={user && isPro ? 'pro' : undefined}
      >
        {user ? (
          user.picture ? (
            <img src={user.picture} alt="" referrerPolicy="no-referrer" />
          ) : (
            initial
          )
        ) : (
          <User />
        )}
      </button>

      {isOpen && (
        <div
          ref={panelRef}
          role="menu"
          aria-label="Account"
          // Solid white: the library's header is blurred itself, and a menu inside it cannot blur the page under it
          className="ns-menu bg-white absolute right-0 top-full mt-2 w-72 z-[200]"
        >
          {/* Account header */}
          <div className="px-2.5 pt-2 pb-3 mb-1.5 border-b border-cream-300">
            {user ? (
              <div className="flex items-center gap-3">
                <span className="ns-avatar [--size:40px]">
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} referrerPolicy="no-referrer" />
                  ) : (
                    initial
                  )}
                </span>
                <div className="flex flex-col min-w-0">
                  <span className="ns-t-title truncate">{user.name}</span>
                  <span className="ns-t-small truncate">{user.email}</span>
                  {isPro && (
                    <span className="ns-chip ns-chip-sm ns-chip-lavender mt-1.5 w-fit">
                      <Crown size={12} /> Pro
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <div>
                <p className="ns-t-title">Guest</p>
                <p className="ns-t-small">Sign in to save your work to the cloud.</p>
              </div>
            )}
          </div>

          {/* Guest actions */}
          {!user && (
            <div className="flex flex-col gap-1.5 p-1">
              {onSignIn && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => { onSignIn(); setIsOpen(false); }}
                  className="ns-btn ns-btn-ghost ns-btn-sm ns-btn-flat w-full"
                >
                  <LogIn size={14} /> Sign in
                </button>
              )}
              {onCreateAccount && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => { onCreateAccount(); setIsOpen(false); }}
                  className="ns-btn ns-btn-primary ns-btn-sm ns-btn-flat w-full"
                >
                  <UserPlus size={14} /> Create account
                </button>
              )}
            </div>
          )}

          {/* Authenticated actions */}
          {user && (
            <>
              {onShare && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => { onShare(); setIsOpen(false); }}
                  className="ns-menu-item"
                >
                  <Share2 /> Share workspace
                </button>
              )}
              {onSubscription && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => { onSubscription(); setIsOpen(false); }}
                  className="ns-menu-item"
                >
                  <Crown /> Plan and credits
                </button>
              )}
              {onAnalytics && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => { onAnalytics(); setIsOpen(false); }}
                  className="ns-menu-item"
                >
                  <PieChart /> Analytics
                </button>
              )}
              {onSettings && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => { onSettings(); setIsOpen(false); }}
                  className="ns-menu-item"
                >
                  <Settings2 /> Settings
                </button>
              )}
              {/* Light, dark or the system's, right here: the one setting worth a switch in the menu */}
              <div className="flex items-center justify-between gap-2 px-2.5 py-1.5" role="group" aria-label="Appearance">
                <span className="flex items-center gap-2.5 text-sm font-medium text-ink"><Sun size={14} className="text-ink-soft" aria-hidden="true" />Appearance</span>
                <span className="ns-seg" data-theme-switch>
                  {THEME_OPTIONS.map(option => (
                    <button
                      key={option.value}
                      type="button"
                      role="radio"
                      aria-checked={theme.setting === option.value}
                      aria-label={option.label}
                      title={option.label}
                      className="ns-seg-item ns-icon-btn ns-icon-btn-xs"
                      onClick={() => theme.setSetting(option.value)}
                    >
                      {option.icon}
                    </button>
                  ))}
                </span>
              </div>
              {onSignOut && (
                <>
                  <div role="separator" className="ns-menu-sep" />
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => { onSignOut(); setIsOpen(false); }}
                    className="ns-menu-item"
                  >
                    <LogOut /> Sign out
                  </button>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
